import { computed } from 'vue';

export function useYearlyCharts(yearlyData, sortedSelectedYears) {
  // Time series trend chart (when > 3 years)
  const trendSeries = computed(() => {
    const years = sortedSelectedYears.value;
    return [
      {
        name: 'Pending',
        type: 'column',
        data: years.map((y) => yearlyData.value[y]?.pending || 0),
      },
      {
        name: 'Completed',
        type: 'column',
        data: years.map((y) => yearlyData.value[y]?.completed || 0),
      },
      {
        name: 'Product Recommends',
        type: 'column',
        data: years.map((y) => yearlyData.value[y]?.productRecommendation || 0),
      },
      {
        name: 'Completion Rate (%)',
        type: 'line',
        data: years.map((y) => {
          const d = yearlyData.value[y];
          if (!d) return 0;
          const total = d.completed + d.pending;
          return total > 0 ? Math.round((d.completed / total) * 100) : 0;
        }),
      },
    ];
  });

  const trendChartOptions = computed(() => ({
    chart: {
      type: 'line',
      stacked: false,
      toolbar: {
        show: true,
        tools: {
          download: true,
          zoom: false,
          zoomin: false,
          zoomout: false,
          pan: false,
          reset: false,
        },
      },
    },
    stroke: {
      width: [0, 0, 0, 3],
      curve: 'smooth',
    },
    plotOptions: {
      bar: {
        columnWidth: '60%',
        borderRadius: 4,
      },
    },
    fill: {
      opacity: [0.85, 0.85, 0.85, 1],
    },
    labels: sortedSelectedYears.value,
    markers: {
      size: [0, 0, 0, 6],
      strokeWidth: 2,
      hover: { size: 8 },
    },
    xaxis: {
      categories: sortedSelectedYears.value,
      labels: {
        style: { fontSize: '12px' },
      },
    },
    yaxis: [
      {
        seriesName: 'Pending',
        title: {
          text: 'Count',
          style: { fontSize: '12px', fontWeight: 600 },
        },
        labels: {
          formatter: (val) => (val ? val.toLocaleString('en-US') : '0'),
          style: { fontSize: '11px' },
        },
      },
      { seriesName: 'Pending', show: false },
      { seriesName: 'Pending', show: false },
      {
        opposite: true,
        seriesName: 'Completion Rate (%)',
        max: 100,
        title: {
          text: 'Completion Rate (%)',
          style: { fontSize: '12px', fontWeight: 600 },
        },
        labels: {
          formatter: (val) => val + '%',
          style: { fontSize: '11px' },
        },
      },
    ],
    tooltip: {
      shared: true,
      intersect: false,
      y: [
        { formatter: (val) => (val ? val.toLocaleString('en-US') : '0') },
        { formatter: (val) => (val ? val.toLocaleString('en-US') : '0') },
        { formatter: (val) => (val ? val.toLocaleString('en-US') : '0') },
        { formatter: (val) => val + '%' },
      ],
    },
    legend: {
      position: 'top',
      horizontalAlign: 'left',
      fontSize: '12px',
      markers: { width: 12, height: 12, radius: 2 },
    },
    colors: ['#FB8C00', '#43A047', '#1976D2', '#E53935'],
    grid: {
      borderColor: '#f1f1f1',
      strokeDashArray: 3,
    },
  }));

  // Product Recommends bar chart
  const productRecommendsSeries = computed(() => [
    {
      name: 'Product Recommends',
      data: sortedSelectedYears.value.map((y) => yearlyData.value[y]?.productRecommendation || 0),
    },
  ]);

  const productRecommendsChartOptions = computed(() => ({
    chart: {
      type: 'bar',
      toolbar: { show: false },
    },
    plotOptions: {
      bar: {
        borderRadius: 6,
        columnWidth: '50%',
        distributed: true,
      },
    },
    dataLabels: {
      enabled: true,
      formatter: (val) => val.toLocaleString('en-US'),
      style: { fontSize: '12px', fontWeight: 600 },
    },
    xaxis: {
      categories: sortedSelectedYears.value,
      labels: { style: { fontSize: '12px', fontWeight: 600 } },
    },
    yaxis: {
      labels: {
        formatter: (val) => (val ? val.toLocaleString('en-US') : '0'),
        style: { fontSize: '11px' },
      },
    },
    legend: { show: false },
    colors: ['#1976D2', '#43A047', '#FB8C00', '#E53935', '#8E24AA', '#00ACC1'],
    grid: { borderColor: '#f1f1f1', strokeDashArray: 3 },
    tooltip: {
      y: { formatter: (val) => val.toLocaleString('en-US') },
    },
  }));

  // Completion Rate chart: completed / (completed + pending)
  const completionRateSeries = computed(() => [
    {
      name: 'Completion Rate',
      data: sortedSelectedYears.value.map((y) => {
        const d = yearlyData.value[y];
        if (!d) return 0;
        const total = d.completed + d.pending;
        return total > 0 ? Math.round((d.completed / total) * 100) : 0;
      }),
    },
  ]);

  const completionRateChartOptions = computed(() => ({
    chart: {
      type: 'bar',
      toolbar: { show: false },
    },
    plotOptions: {
      bar: {
        borderRadius: 6,
        columnWidth: '50%',
        distributed: true,
      },
    },
    dataLabels: {
      enabled: true,
      formatter: (val) => val + '%',
      style: { fontSize: '12px', fontWeight: 600 },
    },
    xaxis: {
      categories: sortedSelectedYears.value,
      labels: { style: { fontSize: '12px', fontWeight: 600 } },
    },
    yaxis: {
      max: 100,
      labels: {
        formatter: (val) => val + '%',
        style: { fontSize: '11px' },
      },
    },
    legend: { show: false },
    colors: ['#43A047', '#1976D2', '#FB8C00', '#E53935', '#8E24AA', '#00ACC1'],
    grid: { borderColor: '#f1f1f1', strokeDashArray: 3 },
    tooltip: {
      y: { formatter: (val) => val + '%' },
    },
  }));

  return {
    trendSeries,
    trendChartOptions,
    productRecommendsSeries,
    productRecommendsChartOptions,
    completionRateSeries,
    completionRateChartOptions,
  };
}
